import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { translate, TranslocoService } from '@jsverse/transloco';

import { AngularSplitModule } from 'angular-split';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { ModalCloseType } from 'app/enums/modal-close-type';
import { WidgetDataSource } from 'app/models/widget-data-source.model';
import { CollectionModule } from 'app/modules/collection/collection.module';
import { DocumentViewOptions } from 'app/modules/collection/components/document/document-view/document-view.component';
import { Dashboard } from 'app/providers/bridge-api/dashboard.service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import {
  Document,
  DocumentService,
  JourneyGroupDocsResult,
  SearchOptions,
} from 'app/providers/storage-api/document.service';
import { Project } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { TimezoneService } from 'app/services/timezone.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';
import { resolveJourneyChannelIcon } from '../journey-channel.utils';

import { NumericMetadataField } from '../../numeric-metadata-field-selector/numeric-metadata-field-selector.models';

export interface JourneyDetailsDialogOptions {
  subjectId: string;
  subjectField: string;
  channelFilter?: JourneyChannelFilter;
  allowedChannelIds?: string[];
  totalTouchpoints: number;
  widgetDataSource: WidgetDataSource;
  numericMetadataFields: NumericMetadataField[];
  dashboard: Dashboard;
  project: Project;
  baseQuery: string;
}

interface JourneyChannelFilter {
  id: string;
  label: string;
}

interface JourneyTouchpointMetadata {
  label: string;
  scoring: string;
  displayValue: string;
  isPositive: boolean;
  isNegative: boolean;
}

interface JourneyTouchpoint {
  document: Document;
  channelId?: string;
  channelLabel: string;
  channelIcon: string;
  timestamp?: Date | string;
  summaryText: string;
  metadataValues: JourneyTouchpointMetadata[];
}

export type NlpDirection = 'top' | 'bottom';
export type NlpSentimentFilter = 'all' | 'positive' | 'negative' | 'neutral';
type NlpOverviewSectionKey = 'entities' | 'phrases' | 'taxonomy' | 'topics';
type NlpSourceItem = Record<string, unknown>;

interface NlpItemFilter {
  type: NlpOverviewSectionKey;
  name: string;
}

interface NlpOverviewItem {
  name: string;
  count: number;
  sentimentScore?: number;
  entityType?: string;
  entityLabel?: string;
  modification?: string;
  topicType?: string;
  strengthScore?: number;
}

interface JourneyDetailsTranslations {
  nlpDirectionTop: string;
  nlpDirectionBottom: string;
  nlpSentimentAll: string;
  nlpSentimentPositive: string;
  nlpSentimentNegative: string;
  nlpSentimentNeutral: string;
}

@Component({
  selector: 'app-journey-details-dialog',
  imports: [AppSharedModule, CollectionModule, AngularSplitModule],
  templateUrl: './journey-details-dialog.component.html',
  styleUrl: './journey-details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class JourneyDetailsDialogComponent implements OnInit {
  options = input.required<JourneyDetailsDialogOptions>();
  close = output<null | ModalCloseType>();
  documentsChanged = output<void>();

  isLoading = signal(false);
  searchText = signal('');
  showDocumentView = signal(false);
  documentViewOptions = signal<DocumentViewOptions | null>(null);
  nlpControlsCollapsed = signal(false);
  nlpDirection = signal<NlpDirection>('top');
  nlpLimit = signal(5);
  nlpSentiment = signal<NlpSentimentFilter>('all');
  nlpActiveFilter = signal<NlpItemFilter | null>(null);
  activeChannelFilter = signal<JourneyChannelFilter | null>(null);
  nlpClosedGroups = signal<string[]>([]);

  private readonly nlpTranslations = signal<JourneyDetailsTranslations>({
    nlpDirectionTop: '',
    nlpDirectionBottom: '',
    nlpSentimentAll: '',
    nlpSentimentPositive: '',
    nlpSentimentNegative: '',
    nlpSentimentNeutral: '',
  });
  readonly nlpDirectionOptions = computed((): DropdownObject[] => {
    const translations = this.nlpTranslations();
    return [
      { id: 'top', title: translations.nlpDirectionTop },
      { id: 'bottom', title: translations.nlpDirectionBottom },
    ];
  });
  readonly nlpSentimentOptions = computed((): DropdownObject[] => {
    const translations = this.nlpTranslations();
    return [
      { id: 'all', title: translations.nlpSentimentAll },
      { id: 'positive', title: translations.nlpSentimentPositive },
      { id: 'negative', title: translations.nlpSentimentNegative },
      { id: 'neutral', title: translations.nlpSentimentNeutral },
    ];
  });

  private allTouchpoints = signal<JourneyTouchpoint[]>([]);
  private sortedDocs: Document[] = [];
  private readonly journeyNlpDocumentId = 'journey-nlp-output';

  displayTouchpoints = computed(() => {
    const term = this.searchText().toLowerCase();
    const nlpFilter = this.nlpActiveFilter();
    const channelFilter = this.activeChannelFilter();
    let touchpoints = this.allTouchpoints();
    touchpoints = touchpoints.filter((tp) => this.matchesAllowedChannelScope(tp.channelId));
    if (channelFilter) {
      touchpoints = touchpoints.filter((tp) =>
        this.matchesChannelFilter(tp.channelId, channelFilter)
      );
    }
    if (term) {
      touchpoints = touchpoints.filter((tp) => this.matchesSearchTerm(tp, term));
    }
    if (nlpFilter) {
      return touchpoints
        .filter((tp) => this.touchpointContainsNlpItem(tp, nlpFilter))
        .map((tp) => ({
          ...tp,
          displaySummary: translate('widgets.journey.journey-details.nlp-appearances', {
            count: this.countNlpItemInTouchpoint(tp, nlpFilter),
            name: nlpFilter.name,
          }),
        }));
    }
    if (term) {
      return touchpoints.map((tp) => ({
        ...tp,
        displaySummary: translate('widgets.journey.journey-details.results', {
          count: this.countSearchTermOccurrences(tp, term),
        }),
      }));
    }
    return touchpoints.map((tp) => ({ ...tp, displaySummary: tp.summaryText }));
  });

  displayTouchpointCount = computed(() => {
    if (this.isLoading() && this.isUsingInitialScope()) {
      return this.options().totalTouchpoints;
    }
    return this.displayTouchpoints().length;
  });

  journeyNlpDocument = computed((): Document => {
    const analysis = this.buildJourneyNlpAnalysis(this.displayTouchpoints());
    return {
      id: this.journeyNlpDocumentId,
      text: '',
      createdAt: new Date(0),
      indexedAt: new Date(0),
      language: '',
      summary: null,
      tags: [],
      version: 1,
      metadata: {},
      analyses: [analysis],
      annotations: [],
    };
  });

  journeyNlpOptions = computed((): DocumentViewOptions => {
    const opts = this.options();
    const sentimentScale = opts.dashboard.payload.sentimentScale;
    return {
      collectionName: opts.widgetDataSource.collectionName ?? '',
      pageIndex: 0,
      documentIndex: 0,
      limit: 1,
      documentId: this.journeyNlpDocumentId,
      projectId: opts.dashboard.project_id,
      sentimentScale,
      sentimentDisplay: opts.dashboard.payload.sentimentDisplay,
      dashboardId: opts.dashboard.id,
      widgetDataSource: opts.widgetDataSource,
      isDashboardLocked: opts.dashboard.locked,
      sentimentRanges: {
        feature:
          opts.project?.polarities?.feature?.[sentimentScale] ??
          SentimentUtils.getDefaultSentimentRanges('feature', sentimentScale),
        document:
          opts.project?.polarities?.document?.[sentimentScale] ??
          SentimentUtils.getDefaultSentimentRanges('document', sentimentScale),
      },
      showNlpData: true,
      nlpFields: [
        { id: 'analyses.entities.title', display: true },
        { id: 'analyses.phrases.title', display: true },
        { id: 'analyses.taxonomy.taxonomyFullPath', display: true },
        { id: 'analyses.themes.normalizedTitle', display: false },
        { id: 'analyses.topics.title', display: true },
        { id: 'analyses.sentimentPolarity', display: false },
      ],
      showMetadata: false,
      metadataFields: [],
      hideOpenNewTabAction: true,
      hideRightSplitArea: false,
    };
  });

  private readonly documentService = inject(DocumentService);
  private readonly alertService = inject(AlertService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translocoService = inject(TranslocoService);

  async ngOnInit(): Promise<void> {
    this.translocoService
      .selectTranslateObject('widgets.journey.journey-details')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translations: Record<string, string>) => {
        this.nlpTranslations.set({
          nlpDirectionTop: translations['nlp-direction-top'],
          nlpDirectionBottom: translations['nlp-direction-bottom'],
          nlpSentimentAll: translations['nlp-sentiment-all'],
          nlpSentimentPositive: translations['nlp-sentiment-positive'],
          nlpSentimentNegative: translations['nlp-sentiment-negative'],
          nlpSentimentNeutral: translations['nlp-sentiment-neutral'],
        });
      });
    this.activeChannelFilter.set(this.options().channelFilter ?? null);
    await this.loadTouchpoints();
  }

  private async loadTouchpoints(): Promise<void> {
    this.isLoading.set(true);
    try {
      const opts = this.options();
      const searchOptions: SearchOptions = {
        sortField: 'createdAt',
        sortOrder: 'ASC',
        timezone: this.timezoneService.timezone,
      };
      const result: JourneyGroupDocsResult =
        await this.documentService.searchForJourneyGroupDocuments(
          opts.widgetDataSource,
          opts.dashboard.project_id,
          opts.baseQuery || undefined,
          {
            ...searchOptions,
            detailed: opts.widgetDataSource.groupAnalyses?.join() || '*',
            journeySubject: {
              subject: opts.subjectField,
              subjectValue: String(opts.subjectId),
            },
          }
        );

      // A content channel is a property of a collection, not of an individual document, so the
      // lookup key is just the collection id.
      const channelMap = result.resultSetAggregation?.collectionIdToContentChannel ?? {};
      const getChannel = (doc: Document) =>
        doc.collectionName ? channelMap[doc.collectionName] : undefined;

      const allDocs = result.items ?? [];
      const scopedDocs = allDocs.filter((doc: Document) =>
        this.matchesAllowedChannelScope(getChannel(doc)?.id)
      );

      this.sortedDocs = [...scopedDocs].sort(
        (a: Document, b: Document) =>
          this.getTimestampSortValue(this.resolveTouchpointTimestamp(a)) -
          this.getTimestampSortValue(this.resolveTouchpointTimestamp(b))
      );

      const touchpoints: JourneyTouchpoint[] = this.sortedDocs.map((doc: Document, idx: number) => {
        const channel = getChannel(doc);
        return {
          document: doc,
          channelId: channel?.id,
          channelLabel: channel?.label ?? '',
          channelIcon: this.resolveChannelIcon(channel?.label ?? ''),
          timestamp: this.resolveTouchpointTimestamp(doc),
          summaryText: this.getSummaryText(doc),
          metadataValues: this.getMetadataValues(doc, idx),
        };
      });

      this.allTouchpoints.set(touchpoints);
    } catch (e) {
      this.alertService.showErrorToast(
        translate('widgets.journey.journey-details.error-loading'),
        e
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  private resolveTouchpointTimestamp(doc: Document): Date | string | undefined {
    return this.isValidTimestamp(doc.createdAt) ? doc.createdAt : undefined;
  }

  private isValidTimestamp(value: unknown): boolean {
    if (!value) {
      return false;
    }
    return !Number.isNaN(new Date(value as string | number | Date).getTime());
  }

  private getTimestampSortValue(timestamp?: Date | string): number {
    if (!timestamp) {
      return Number.MAX_SAFE_INTEGER;
    }
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
  }

  private resolveChannelIcon(channelLabel: string): string {
    return resolveJourneyChannelIcon(channelLabel);
  }

  private matchesChannelFilter(
    channelId?: string,
    channelFilter: JourneyChannelFilter | null = this.activeChannelFilter()
  ): boolean {
    return !channelFilter || channelId === channelFilter.id;
  }

  private matchesAllowedChannelScope(channelId?: string): boolean {
    const allowedChannelIds = this.options().allowedChannelIds ?? [];
    if (allowedChannelIds.length === 0) {
      return true;
    }
    return !!channelId && allowedChannelIds.includes(channelId);
  }

  private getSummaryText(doc: Document): string {
    const analysis = doc.analyses?.[0];
    const firstSection = analysis?.flatSections?.[0];
    if (firstSection?.value) {
      return Utils.truncateEnd(firstSection.value, 300);
    }
    if (analysis?.redactedSummary) {
      return Utils.truncateEnd(analysis.redactedSummary, 300);
    }
    if (doc.text) {
      return Utils.truncateEnd(this.extractFirstSectionText(doc.text), 300);
    }
    return translate('widgets.journey.journey-details.no-summary');
  }

  private extractFirstSectionText(text: string): string {
    const trimmed = text.trimStart();
    const stripped = trimmed.replace(
      /^adtalemSectionTitle:\s*.+?\s+-\s+\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}\s+-\s+\w+:\s*/,
      ''
    );
    return stripped.split(/\n\s*(?:adtalemSectionTitle:|AGENT NOTES)/)[0].trim();
  }

  private getMetadataValues(doc: Document, chronIdx: number): JourneyTouchpointMetadata[] {
    return this.options().numericMetadataFields.map((field) => {
      let raw = doc.metadata?.[field.fieldId];
      if (field.fieldId.includes('sentiment')) {
        raw = doc.analyses[0].sentimentScore;
      }
      const rawValue = typeof raw === 'number' ? raw : null;
      const label = field.label ?? field.fieldId;

      if (field.scoring === 'DELTA') {
        if (chronIdx === 0 || rawValue == null) {
          return {
            label,
            scoring: field.scoring,
            displayValue: '–',
            isPositive: false,
            isNegative: false,
          };
        }
        let prevRaw = this.sortedDocs[chronIdx - 1].metadata?.[field.fieldId];
        if (field.fieldId.includes('sentiment')) {
          prevRaw = this.sortedDocs[chronIdx - 1].analyses[0].sentimentScore;
        }
        const prevValue = typeof prevRaw === 'number' ? prevRaw : null;
        if (prevValue == null) {
          return {
            label,
            scoring: field.scoring,
            displayValue: '–',
            isPositive: false,
            isNegative: false,
          };
        }
        const delta = rawValue - prevValue;
        const displayValue = delta >= 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2);
        return {
          label,
          scoring: field.scoring,
          displayValue,
          isPositive: delta > 0,
          isNegative: delta < 0,
        };
      }

      return {
        label,
        scoring: field.scoring,
        displayValue: rawValue != null ? rawValue.toFixed(2) : '–',
        isPositive: (rawValue ?? 0) > 0,
        isNegative: (rawValue ?? 0) < 0,
      };
    });
  }

  private isUsingInitialScope(): boolean {
    return (
      !this.searchText() &&
      !this.nlpActiveFilter() &&
      this.areChannelFiltersEqual(this.activeChannelFilter(), this.options().channelFilter ?? null)
    );
  }

  private areChannelFiltersEqual(
    left: JourneyChannelFilter | null,
    right: JourneyChannelFilter | null
  ): boolean {
    if (!left || !right) {
      return left === right;
    }
    return left.id === right.id;
  }

  private getNlpItems(analysis: DocumentAnalysis, type: NlpOverviewSectionKey): NlpSourceItem[] {
    const itemsByType: Record<NlpOverviewSectionKey, unknown[] | undefined> = {
      entities: analysis.entities,
      phrases: analysis.phrases,
      taxonomy: analysis.taxonomy,
      topics: analysis.topics,
    };
    return this.normalizeNlpItems(itemsByType[type]);
  }

  private normalizeNlpItems(items: unknown[] | undefined): NlpSourceItem[] {
    return (items ?? []).filter(
      (item: unknown): item is NlpSourceItem => typeof item === 'object' && item !== null
    );
  }

  private getNlpItemName(type: NlpOverviewSectionKey, item: NlpSourceItem): string {
    if (type === 'taxonomy') {
      return (
        this.getTextProperty(item, 'taxonomyFullPath') || this.getTextProperty(item, 'taxonomyPath')
      );
    }
    return this.getTextProperty(item, 'title');
  }

  private getNlpItemSentiment(item: NlpSourceItem): number | undefined {
    const sentimentScore = item['sentimentScore'];
    return typeof sentimentScore === 'number' ? sentimentScore : undefined;
  }

  private getNlpItemStrengthScore(item: NlpSourceItem): number | undefined {
    const strengthScore = item['strengthScore'];
    return typeof strengthScore === 'number' ? strengthScore : undefined;
  }

  private getTopicType(item: NlpSourceItem): string | undefined {
    return this.getTextProperty(item, 'type') || undefined;
  }

  private getPhraseModification(item: NlpSourceItem): string | undefined {
    const parts: string[] = [];
    if (item['isIntensified'] === true) {
      parts.push('intensified');
    }
    if (item['isNegated'] === true) {
      parts.push('negated');
    }
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  private getTextProperty(item: NlpSourceItem, key: string): string {
    const value = item[key];
    return typeof value === 'string' ? value : '';
  }

  private aggregateNlpItems(
    touchpoints: JourneyTouchpoint[],
    extract: (analysis: DocumentAnalysis) => NlpSourceItem[],
    getName: (item: NlpSourceItem) => string,
    getSentimentScore: (item: NlpSourceItem) => number | undefined,
    sentimentFilter: NlpSentimentFilter,
    direction: NlpDirection,
    limit: number,
    getExtraFields?: (item: NlpSourceItem) => Partial<NlpOverviewItem>
  ): NlpOverviewItem[] {
    const counts = new Map<string, NlpOverviewItem>();
    for (const tp of touchpoints) {
      const analysis = tp.document.analyses?.[0];
      if (!analysis) {
        continue;
      }
      for (const item of extract(analysis)) {
        const name = getName(item);
        if (!name) {
          continue;
        }
        const score = getSentimentScore(item);
        if (!this.matchesSentimentFilter(score, sentimentFilter)) {
          continue;
        }
        if (!counts.has(name)) {
          counts.set(name, { name, count: 0, sentimentScore: score, ...getExtraFields?.(item) });
        }
        counts.get(name)!.count++;
      }
    }
    const items = Array.from(counts.values());
    const sortDir = direction === 'top' ? -1 : 1;
    return items.sort((a, b) => sortDir * (a.count - b.count)).slice(0, limit);
  }

  private buildJourneyNlpAnalysis(touchpoints: JourneyTouchpoint[]): DocumentAnalysis {
    const direction = this.nlpDirection();
    const limit = this.nlpLimit();
    const sentiment = this.nlpSentiment();
    return {
      analysisName: 'journey-nlp-output',
      analysisType: 'journey',
      annotator: 'journey-details-dialog',
      configId: '',
      displayName: 'Journey NLP Output',
      documentId: this.journeyNlpDocumentId,
      entities: this.aggregateNlpItems(
        touchpoints,
        (analysis: DocumentAnalysis) => this.getNlpItems(analysis, 'entities'),
        (item: NlpSourceItem) => this.getNlpItemName('entities', item),
        (item: NlpSourceItem) => this.getNlpItemSentiment(item),
        sentiment,
        direction,
        limit,
        (item: NlpSourceItem) => ({
          entityType: this.getTextProperty(item, 'entityType') || undefined,
          entityLabel: this.getTextProperty(item, 'label') || undefined,
        })
      ).map((item: NlpOverviewItem) => ({
        title: `${item.name} (${item.count})`,
        entityType: item.entityType,
        label: item.entityLabel,
        sentimentScore: item.sentimentScore,
      })),
      languageScore: 0,
      phrases: this.aggregateNlpItems(
        touchpoints,
        (analysis: DocumentAnalysis) => this.getNlpItems(analysis, 'phrases'),
        (item: NlpSourceItem) => this.getNlpItemName('phrases', item),
        (item: NlpSourceItem) => this.getNlpItemSentiment(item),
        sentiment,
        direction,
        limit,
        (item: NlpSourceItem) => ({ modification: this.getPhraseModification(item) })
      ).map((item: NlpOverviewItem) => ({
        title: `${item.name} (${item.count})`,
        modification: item.modification,
        sentimentScore: item.sentimentScore,
      })),
      projectId: this.options().dashboard.project_id,
      sentimentPolarity: 'neutral',
      sentimentPolarity3: 'neutral',
      sentimentPolarity5: 'neutral',
      sentimentScore: 0,
      summary: '',
      themes: [],
      topics: this.aggregateNlpItems(
        touchpoints,
        (analysis: DocumentAnalysis) => this.getNlpItems(analysis, 'topics'),
        (item: NlpSourceItem) => this.getNlpItemName('topics', item),
        (item: NlpSourceItem) => this.getNlpItemSentiment(item),
        sentiment,
        direction,
        limit,
        (item: NlpSourceItem) => ({
          topicType: this.getTopicType(item),
          strengthScore: this.getNlpItemStrengthScore(item),
        })
      ).map((item: NlpOverviewItem) => ({
        title: `${item.name} (${item.count})`,
        type: item.topicType,
        strengthScore: item.strengthScore,
        sentimentScore: item.sentimentScore,
      })),
      taxonomy: this.aggregateNlpItems(
        touchpoints,
        (analysis: DocumentAnalysis) => this.getNlpItems(analysis, 'taxonomy'),
        (item: NlpSourceItem) => this.getNlpItemName('taxonomy', item),
        () => undefined,
        'all',
        direction,
        limit
      ).map((item: NlpOverviewItem) => ({
        title: `${item.name} (${item.count})`,
        taxonomyFullPath: `${item.name} (${item.count})`,
      })),
      flatSections: [],
    };
  }

  private getSectionKeyForAnalyticItem(analyticItem: string): NlpOverviewSectionKey | null {
    switch (analyticItem) {
      case 'analyses.entities.title':
      case 'ML-analyses.entities.title':
        return 'entities';
      case 'analyses.phrases.title':
        return 'phrases';
      case 'analyses.taxonomy.taxonomyFullPath':
        return 'taxonomy';
      case 'analyses.topics.title':
        return 'topics';
      default:
        return null;
    }
  }

  private matchesSentimentFilter(score: number | undefined, filter: NlpSentimentFilter): boolean {
    if (filter === 'all') {
      return true;
    }

    if (score === undefined) {
      return filter === 'neutral';
    }

    const ranges = this.journeyNlpOptions().sentimentRanges.feature;
    const scale = this.journeyNlpOptions().sentimentScale;
    const sentimentPolarity = SentimentUtils.getSentimentValueFromRange(score, ranges, scale);
    return filter === sentimentPolarity;
  }

  private touchpointContainsNlpItem(tp: JourneyTouchpoint, filter: NlpItemFilter): boolean {
    const analysis = tp.document.analyses?.[0];
    if (!analysis) {
      return false;
    }
    const nameLower = filter.name.toLowerCase();
    return this.getNlpItems(analysis, filter.type).some(
      (item: NlpSourceItem) => this.getNlpItemName(filter.type, item).toLowerCase() === nameLower
    );
  }

  private countNlpItemInTouchpoint(tp: JourneyTouchpoint, filter: NlpItemFilter): number {
    const analysis = tp.document.analyses?.[0];
    if (!analysis) {
      return 0;
    }
    const nameLower = filter.name.toLowerCase();
    return this.getNlpItems(analysis, filter.type).filter(
      (item: NlpSourceItem) => this.getNlpItemName(filter.type, item).toLowerCase() === nameLower
    ).length;
  }

  private countSearchTermOccurrences(tp: JourneyTouchpoint, term: string): number {
    const text = (tp.document.text ?? '').toLowerCase();
    if (!text || !term) {
      return 0;
    }
    let count = 0;
    let pos = 0;
    while ((pos = text.indexOf(term, pos)) !== -1) {
      count++;
      pos += term.length;
    }
    return count;
  }

  private matchesSearchTerm(tp: JourneyTouchpoint, term: string): boolean {
    const normalizedTerm = term.trim();
    if (!normalizedTerm) {
      return true;
    }
    const haystacks = [
      tp.channelLabel,
      tp.summaryText,
      tp.document.text ?? '',
      ...tp.metadataValues.map(
        (metadataValue: JourneyTouchpointMetadata) =>
          `${metadataValue.label} ${metadataValue.displayValue}`
      ),
    ];
    return haystacks.some((value: string) => value.toLowerCase().includes(normalizedTerm));
  }

  onSearchChanged(text: string): void {
    this.searchText.set(text.trim());
  }

  onNlpItemClick(sectionKey: NlpOverviewSectionKey, itemName: string): void {
    const current = this.nlpActiveFilter();
    if (current?.type === sectionKey && current.name === itemName) {
      this.nlpActiveFilter.set(null);
    } else {
      this.nlpActiveFilter.set({ type: sectionKey, name: itemName });
    }
  }

  onNlpFilterRemoved(): void {
    this.nlpActiveFilter.set(null);
  }

  onChannelFilterRemoved(): void {
    this.activeChannelFilter.set(null);
  }

  onChannelClick(event: MouseEvent, tp: JourneyTouchpoint): void {
    event.stopPropagation();
    if (!tp.channelId) {
      return;
    }
    this.activeChannelFilter.set({
      id: tp.channelId,
      label: tp.channelLabel,
    });
  }

  onNlpClosedGroupsChange(groups: string[]): void {
    this.nlpClosedGroups.set(groups);
  }

  onJourneyNlpSelection(selection: { analyticItem: string; itemTitle: string }): void {
    const sectionKey = this.getSectionKeyForAnalyticItem(selection.analyticItem);
    if (!sectionKey) {
      return;
    }
    const cleanTitle = selection.itemTitle.replace(/\s*\(\d+\)$/, '');
    this.onNlpItemClick(sectionKey, cleanTitle);
  }

  onNlpControlsToggled(): void {
    this.nlpControlsCollapsed.update((value: boolean) => !value);
  }

  onNlpDirectionChanged(value: string): void {
    if (value === 'top' || value === 'bottom') {
      this.nlpDirection.set(value);
    }
  }

  onNlpLimitChanged(value: number | string): void {
    const normalizedValue = Number(value);
    if (!Number.isFinite(normalizedValue)) {
      return;
    }
    const boundedValue = Math.min(50, Math.max(1, Math.floor(normalizedValue)));
    this.nlpLimit.set(boundedValue);
  }

  onNlpSentimentChanged(value: string): void {
    if (value === 'all' || value === 'positive' || value === 'negative' || value === 'neutral') {
      this.nlpSentiment.set(value);
    }
  }

  openDocument(tp: JourneyTouchpoint): void {
    const opts = this.options();
    const sentimentScale = opts.dashboard.payload.sentimentScale;
    const doc = tp.document;

    this.documentViewOptions.set({
      collectionName: doc.collectionName,
      page: { count: 1, items: [doc] },
      pageIndex: 0,
      documentIndex: 0,
      limit: 1,
      documentId: doc.id,
      pageItemSupplier: (documentId: string) =>
        this.documentService.getById(
          { type: 'collection', id: doc.collectionName ?? opts.widgetDataSource.collectionName },
          documentId,
          doc.analyses?.[0]?.analysisName ? [doc.analyses[0].analysisName] : undefined,
          opts.dashboard.project_id
        ),
      projectId: opts.dashboard.project_id,
      sentimentScale,
      sentimentDisplay: opts.dashboard.payload.sentimentDisplay,
      dashboardId: opts.dashboard.id,
      widgetDataSource: opts.widgetDataSource,
      isDashboardLocked: opts.dashboard.locked,
      sentimentRanges: {
        feature:
          opts.project?.polarities?.feature?.[sentimentScale] ??
          SentimentUtils.getDefaultSentimentRanges('feature', sentimentScale),
        document:
          opts.project?.polarities?.document?.[sentimentScale] ??
          SentimentUtils.getDefaultSentimentRanges('document', sentimentScale),
      },
      dataSourceType: opts.widgetDataSource.type,
      analysisGroupId: opts.widgetDataSource.groupId,
      showNlpData: true,
      hideOpenNewTabAction: true,
      initialSearchTerm: this.searchText() || undefined,
      backAction: {
        label: translate('widgets.journey.journey-details.see-full-journey'),
      },
    });
    this.showDocumentView.set(true);
  }

  async onDocumentViewClosed(hasChanged = false): Promise<void> {
    this.showDocumentView.set(false);
    this.documentViewOptions.set(null);
    if (hasChanged) {
      await this.loadTouchpoints();
      this.documentsChanged.emit();
    }
  }

  onClose(): void {
    this.close.emit(null);
  }

  trackTouchpoints(document: Document): string {
    return `${document.id}-${document.collectionName}`;
  }
}
